/**
 * The Yanyu region's greybox (docs/archive/REGION_GREYBOX.md): every place of docs/archive/OPEN_WORLD.md §2.2
 * blocked out as plain boxes at its position, footprint and height. `region/generate.ts` stamps
 * them into the chunks as solid masses with standable tops (walls, halls, towers, tiers, stones)
 * or walked surfaces (terraces, ramps, landings, piers, pits). Detail lanes replace boxes by id.
 *
 * Box ids are stable. The stone forest's spires, the pagodas on some of them, and the bay's
 * stepping stones come from the sketch (`regionBoxes` in region/generate.ts adds them).
 *
 * Data only: the few loops below lay out repeated boxes (wall runs, stair legs, ledges).
 */
import type { BlockoutBox } from '../../region/sketch';
import type { Facing } from '../../map';
import { YANYU_CITY_BOXES } from './yanyu.city';
import { YANYU_QINGGONG_BOXES } from './yanyu.qinggong';

type Box = BlockoutBox;
const boxes: Box[] = [];
const add = (b: Box): void => {
  boxes.push(b);
};

/**
 * A straight wall from `a` to `b` along x or y, `t` thick and `h` high, broken at `gaps` (the
 * gates: [from, to) along the run). Each piece is a box `<id>.<n>`.
 */
function wallRun(
  id: string,
  place: number,
  name: string,
  lane: string,
  axis: 'x' | 'y',
  fixed: number,
  a: number,
  b: number,
  t: number,
  h: number,
  gaps: readonly (readonly [number, number])[],
  kind: Box['kind'] = 'wall',
): void {
  const cuts = [a, ...gaps.flat(), b];
  for (let k = 0; k + 1 < cuts.length; k += 2) {
    const u0 = cuts[k]!;
    const u1 = cuts[k + 1]!;
    if (u1 <= u0) continue;
    const c = (u0 + u1) / 2;
    const L = u1 - u0;
    add({
      id: `${id}.${k / 2 + 1}`,
      place,
      name,
      lane,
      kind,
      h,
      ...(axis === 'x' ? { x: c, y: fixed, w: L, d: t } : { x: fixed, y: c, w: t, d: L }),
    });
  }
}

/** Two gate pillars either side of a gap. */
function pillars(id: string, place: number, name: string, lane: string, pts: readonly (readonly [number, number])[], s: number, h: number): void {
  pts.forEach(([x, y], k) => add({ id: `${id}.${k + 1}`, place, name, lane, kind: 'gate', x, y, w: s, d: s, h }));
}

// ── 2 · ลานศิลาเก้ากระบี่ (the hub city) and 15 · เจดีย์เก้าชั้น: yanyu.city.ts ──────────────────
boxes.push(...YANYU_CITY_BOXES);

// ── 3 · Wudang's compound (ลานหอเมฆา) on its 185 m flat ─────────────────────────────────────
{
  const L = 'mist-peak';
  add({ id: 'mist-peak.terrace.1', place: 3, name: 'ลานหอเมฆา ลานชั้นล่าง', lane: L, kind: 'terrace', x: 710, y: 1030, w: 100, d: 70, h: 2.4 });
  add({ id: 'mist-peak.ramp.1', place: 3, name: 'ลานหอเมฆา ทางขึ้นลานชั้นล่าง', lane: L, kind: 'ramp', down: 'n', x: 710, y: 990, w: 10, d: 10, h: 2.4 });
  add({ id: 'mist-peak.terrace.2', place: 3, name: 'ลานหอเมฆา ลานชั้นบน', lane: L, kind: 'terrace', on: 'mist-peak.terrace.1', x: 710, y: 1043, w: 70, d: 40, h: 2.4 });
  add({ id: 'mist-peak.ramp.2', place: 3, name: 'ลานหอเมฆา ทางขึ้นลานชั้นบน', lane: L, kind: 'ramp', down: 'n', on: 'mist-peak.terrace.1', x: 710, y: 1018, w: 10, d: 10, h: 2.4 });
  add({ id: 'mist-peak.hall.main', place: 3, name: 'หอเมฆา (ตำหนักใหญ่)', lane: L, kind: 'hall', on: 'mist-peak.terrace.2', x: 710, y: 1050, w: 36, d: 20, h: 14 });
  add({ id: 'mist-peak.hall.east', place: 3, name: 'ลานหอเมฆา ตำหนักตะวันออก', lane: L, kind: 'hall', on: 'mist-peak.terrace.1', x: 745, y: 1010, w: 16, d: 12, h: 9 });
  add({ id: 'mist-peak.hall.west', place: 3, name: 'ลานหอเมฆา ตำหนักตะวันตก', lane: L, kind: 'hall', on: 'mist-peak.terrace.1', x: 675, y: 1010, w: 16, d: 12, h: 9 });
  add({ id: 'mist-peak.tower.bell', place: 3, name: 'หอระฆังยอดหมอก', lane: L, kind: 'tower', x: 680, y: 972, w: 8, d: 8, h: 12 });
  add({ id: 'mist-peak.tower.drum', place: 3, name: 'หอกลองยอดหมอก', lane: L, kind: 'tower', x: 740, y: 972, w: 8, d: 8, h: 12 });
}

// ── บันไดหมื่นขั้น: the foot village (40 m) to the compound (185 m) ────────────────────────────
// Nine switchback legs of 52 m between landings, 6 m wide, rising 16.1 m each (0.31 m a metre,
// under the 0.35 m step), stacked south-west up the slope, then a walk at 185 m into the compound.
{
  const L = 'mist-peak';
  const N = 'บันไดหมื่นขั้น';
  const legs = 9;
  const rise = (185 - 40) / legs;
  const lv = (k: number) => 40 + k * rise;
  const y0 = (k: number) => 870 + 7 * k;
  add({ id: 'mist-peak.steps.landing.0', place: 3, name: `${N} เชิงบันได`, lane: L, kind: 'landing', x: 884, y: y0(0) + 3, w: 12, d: 6, h: 0, base: lv(0) });
  for (let k = 0; k < legs; k++) {
    const down: Facing = k % 2 === 0 ? 'e' : 'w';
    add({ id: `mist-peak.steps.leg.${k + 1}`, place: 3, name: `${N} ช่วง ${k + 1}`, lane: L, kind: 'ramp', down, x: 852, y: y0(k) + 3, w: 52, d: 6, h: rise, base: lv(k) });
    const west = k % 2 === 0;
    const last = k === legs - 1;
    add({
      id: `mist-peak.steps.landing.${k + 1}`,
      place: 3,
      name: `${N} ชานพัก ${k + 1}`,
      lane: L,
      kind: 'landing',
      x: west ? 820 : 884,
      y: last ? y0(k) + 3 : (y0(k) + y0(k + 1) + 6) / 2,
      w: 12,
      d: last ? 6 : 13,
      h: 0,
      base: lv(k + 1),
    });
  }
  add({ id: 'mist-peak.steps.walk.1', place: 3, name: `${N} ทางเดินบนสันเขา`, lane: L, kind: 'landing', x: 795, y: y0(legs - 1) + 3, w: 38, d: 6, h: 0, base: 185 });
  add({ id: 'mist-peak.steps.walk.2', place: 3, name: `${N} ทางเข้าลานหอเมฆา`, lane: L, kind: 'landing', x: 779, y: 948, w: 6, d: 40, h: 0, base: 185, note: 'meets the compound flat at 185 m' });
}

// ── 4 · ยอดทองแห่งยอดหมอก (the golden summit, 380 m) ────────────────────────────────────────────────
add({ id: 'summit.hall', place: 4, name: 'ตำหนักยอดทอง', lane: 'mist-peak', kind: 'hall', x: 520, y: 1000, w: 16, d: 12, h: 10, note: 'the great glide launch; reached by qinggong' });

// ── 5 · Wudang's foot village (40 m) ─────────────────────────────────────────────────────────
{
  const L = 'mist-peak';
  const N = 'หมู่บ้านเชิงเขายอดหมอก';
  const houses: [number, number][] = [
    [925, 800],
    [995, 800],
    [925, 845],
    [1010, 860],
  ];
  houses.forEach(([x, y], k) => add({ id: `wudangfoot.house.${k + 1}`, place: 5, name: `${N} บ้าน ${k + 1}`, lane: L, kind: 'house', x, y, w: 12, d: 8, h: 5 }));
  add({ id: 'wudangfoot.station', place: 5, name: 'ท่ารถม้าเชิงเขายอดหมอก', lane: 'roads', kind: 'station', x: 985, y: 832, w: 10, d: 6, h: 4 });
}

// ── 7 · หุบเขาจันทร์มัว: the lakeside town ────────────────────────────────────────────────────
{
  const L = 'valley';
  const N = 'เมืองริมทะเลสาบสราญ';
  const houses: [number, number][] = [
    [2110, 1520],
    [2190, 1520],
    [2110, 1600],
    [2190, 1600],
  ];
  houses.forEach(([x, y], k) => add({ id: `lake.house.${k + 1}`, place: 7, name: `${N} เรือน ${k + 1}`, lane: L, kind: 'house', x, y, w: 20, d: 12, h: 7 }));
  add({ id: 'lake.hall', place: 7, name: `${N} หอใหญ่`, lane: L, kind: 'hall', x: 2150, y: 1515, w: 16, d: 12, h: 9 });
  add({ id: 'lake.pier', place: 7, name: `${N} ท่าเรือ`, lane: L, kind: 'pier', x: 2225, y: 1560, w: 40, d: 4, h: 0.75, base: 30, note: 'a skimmer steps up onto it (under STEP_UP)' });
  add({ id: 'lake.station', place: 7, name: 'ท่ารถม้าทะเลสาบสราญ', lane: 'roads', kind: 'station', x: 2140, y: 1592, w: 10, d: 6, h: 4 });
}

// ── 8 · ทะเลไผ่ (the bamboo sea, 15–70 m): a stele on the hill east of แม่น้ำสราญ ─────────────────
add({ id: 'bamboo.stele', place: 8, name: 'ทะเลไผ่ ศิลาจารึก', lane: 'wilds', kind: 'stone', x: 2080, y: 880, w: 3, d: 2, h: 5, note: 'a marker in the bamboo, 40 m east of the river; a perch over the canopy' });

// ── 9 · ศาลาพักม้าสิบลี้ (the ten-li waystation), 120 m north of where it was, clear of the city ─
add({ id: 'waystation.hall', place: 9, name: 'ศาลาพักม้าสิบลี้', lane: 'roads', kind: 'hall', x: 1515, y: 915, w: 16, d: 10, h: 6 });
add({ id: 'waystation.stable', place: 9, name: 'ศาลาพักม้าสิบลี้ คอกม้า', lane: 'roads', kind: 'house', x: 1517, y: 950, w: 12, d: 6, h: 4 });
add({ id: 'waystation.station', place: 9, name: 'ท่ารถม้าศาลาพักม้าสิบลี้', lane: 'roads', kind: 'station', x: 1532, y: 935, w: 8, d: 6, h: 4 });

// ── 10 · ค่ายโจรภูเขา (the bandit camp), deeper in the hills: a palisade 100 × 80 m, gate east ──
{
  const L = 'wilds';
  const N = 'ค่ายโจรภูเขา';
  wallRun('bandits.palisade.n', 10, `${N} รั้วค่าย`, L, 'x', 2121, 650, 750, 2, 4, [], 'camp');
  wallRun('bandits.palisade.s', 10, `${N} รั้วค่าย`, L, 'x', 2199, 650, 750, 2, 4, [], 'camp');
  wallRun('bandits.palisade.w', 10, `${N} รั้วค่าย`, L, 'y', 651, 2122, 2198, 2, 4, [], 'camp');
  wallRun('bandits.palisade.e', 10, `${N} รั้วค่าย`, L, 'y', 749, 2122, 2198, 2, 4, [[2154, 2166]], 'camp');
  const tents: [number, number][] = [
    [680, 2145],
    [720, 2145],
    [680, 2180],
  ];
  tents.forEach(([x, y], k) => add({ id: `bandits.tent.${k + 1}`, place: 10, name: `${N} กระโจม ${k + 1}`, lane: L, kind: 'tent', x, y, w: 6, d: 5, h: 3 }));
  add({ id: 'bandits.tower', place: 10, name: `${N} หอสังเกตการณ์`, lane: L, kind: 'tower', x: 735, y: 2135, w: 4, d: 4, h: 10 });
}

// ── 11 · ซากวัดกระบี่ทมิฬ (the black sword's ruined temple), 500 m south of the city's wall ───
{
  const L = 'wilds';
  const N = 'ซากวัดกระบี่ทมิฬ';
  add({ id: 'blacktemple.hall', place: 11, name: `${N} วิหารพัง`, lane: L, kind: 'ruin', x: 1720, y: 2500, w: 30, d: 20, h: 8 });
  add({ id: 'blacktemple.wall.w', place: 11, name: `${N} กำแพงพัง`, lane: L, kind: 'ruin', x: 1670, y: 2520, w: 2, d: 60, h: 3 });
  add({ id: 'blacktemple.wall.e', place: 11, name: `${N} กำแพงพัง`, lane: L, kind: 'ruin', x: 1770, y: 2520, w: 2, d: 60, h: 3 });
  add({ id: 'blacktemple.pagoda', place: 11, name: `${N} เจดีย์หัก`, lane: L, kind: 'tower', x: 1760, y: 2555, w: 8, d: 8, h: 14 });
  pillars('blacktemple.gate', 11, `${N} ประตู`, L, [[1712, 2562], [1728, 2562]], 3, 7);
}

// ── 12 · ป้อมร้างผาหัวมังกร (the dragon-head fort, 60 m over the sea) ─────────────────────────
{
  const L = 'islands';
  const N = 'ป้อมร้างผาหัวมังกร';
  wallRun('dragonfort.wall.n', 12, `${N} กำแพง`, L, 'x', 117, 1050, 1110, 3, 6, []);
  wallRun('dragonfort.wall.s', 12, `${N} กำแพง`, L, 'x', 163, 1050, 1110, 3, 6, [[1074, 1086]]);
  wallRun('dragonfort.wall.w', 12, `${N} กำแพง`, L, 'y', 1051, 119, 161, 3, 6, []);
  wallRun('dragonfort.wall.e', 12, `${N} กำแพง`, L, 'y', 1109, 119, 161, 3, 6, []);
  add({ id: 'dragonfort.keep', place: 12, name: `${N} หอรบ`, lane: L, kind: 'tower', x: 1088, y: 135, w: 12, d: 12, h: 14 });
}

// ── 13 · ประภาคารเก่า (the old lighthouse): three tiers to 26 m on its 16 m island ───────────────
[
  [14, 10],
  [10, 18],
  [6, 26],
].forEach(([s, h], k) =>
  add({ id: `lighthouse.tier.${k + 1}`, place: 13, name: `ประภาคารเก่า ชั้น ${k + 1}`, lane: 'islands', kind: 'tier', x: 1760, y: 120, w: s!, d: s!, h: h! }),
);

// ── 14 · อ่าวโจรสลัด (the pirate cove) ──────────────────────────────────────────────────────
{
  const L = 'islands';
  const N = 'อ่าวโจรสลัด';
  const tents: [number, number][] = [
    [1995, 185],
    [2025, 185],
    [2010, 205],
  ];
  tents.forEach(([x, y], k) => add({ id: `pirates.tent.${k + 1}`, place: 14, name: `${N} กระโจม ${k + 1}`, lane: L, kind: 'tent', x, y, w: 6, d: 5, h: 3 }));
  add({ id: 'pirates.lookout', place: 14, name: `${N} หอดูเรือ`, lane: L, kind: 'tower', x: 2040, y: 150, w: 4, d: 4, h: 12 });
  add({ id: 'pirates.pier', place: 14, name: `${N} ท่าเรือ`, lane: L, kind: 'pier', x: 2010, y: 290, w: 4, d: 30, h: 0.75, base: 0, note: 'a skimmer steps up onto it (under STEP_UP)' });
}

// ── 16 · น้ำตกเมฆา (the cloud falls): wall-run ledges every ~7 m beside the 105 m drop ───────────
for (let k = 0; k <= 15; k++) {
  const t = k / 15;
  add({
    id: `falls.ledge.${k + 1}`,
    place: 16,
    name: `น้ำตกเมฆา ชะง่อนผา ${k + 1}`,
    lane: 'mist-peak',
    kind: 'ledge',
    x: Math.round(760 + (640 - 760) * t + 14 * 0.447),
    y: Math.round(760 + (820 - 760) * t + 14 * 0.894),
    w: 6,
    d: 4,
    h: 1.5,
    note: 'a perch 1.5 m over the slope beside the falls',
  });
}

// ── 17 · เหวกระโจน (the leaping gorge): 30 m across, 60 m deep ─────────────────────────────────
add({ id: 'gorge.pit', place: 17, name: 'เหวกระโจน', lane: 'valley', kind: 'pit', x: 2660, y: 1650, w: 30, d: 200, h: -60 });
add({ id: 'gorge.runup.w', place: 17, name: 'เหวกระโจน จุดวิ่งกระโจน', lane: 'valley', kind: 'landing', x: 2638, y: 1650, w: 6, d: 10, h: 0.3 });
add({ id: 'gorge.runup.e', place: 17, name: 'เหวกระโจน จุดลงเท้า', lane: 'valley', kind: 'landing', x: 2682, y: 1650, w: 6, d: 10, h: 0.3 });

// ── 18 · สุสานใต้เขา (the graves under the mountain) ──────────────────────────────────────────
add({ id: 'graves.shrine', place: 18, name: 'สุสานใต้เขา ศาลเจ้า', lane: 'mist-peak', kind: 'hall', x: 760, y: 1428, w: 8, d: 6, h: 5 });
for (let k = 0; k < 6; k++)
  add({
    id: `graves.mound.${k + 1}`,
    place: 18,
    name: `สุสานใต้เขา หลุมศพ ${k + 1}`,
    lane: 'mist-peak',
    kind: 'mound',
    x: 742 + (k % 3) * 18,
    y: 1450 + Math.floor(k / 3) * 14,
    w: 3,
    d: 2,
    h: 1.2,
  });

// ── 19 · วัดร้างในป่าไผ่ (the bamboo temple) ──────────────────────────────────────────────────
add({ id: 'bambootemple.hall', place: 19, name: 'วัดร้างในป่าไผ่ วิหาร', lane: 'wilds', kind: 'ruin', x: 2230, y: 975, w: 20, d: 14, h: 7 });
add({ id: 'bambootemple.wall', place: 19, name: 'วัดร้างในป่าไผ่ กำแพงพัง', lane: 'wilds', kind: 'ruin', x: 2230, y: 996, w: 30, d: 2, h: 3 });
add({ id: 'bambootemple.stupa', place: 19, name: 'วัดร้างในป่าไผ่ สถูป', lane: 'wilds', kind: 'tower', x: 2252, y: 962, w: 4, d: 4, h: 8 });

// ── 20 · ทุ่งหมาป่าเงาจันทร์ (the moon-shadow wolf fields), between the city and the bamboo sea ─
add({ id: 'wolves.den', place: 20, name: 'ทุ่งหมาป่าเงาจันทร์ ถ้ำหมาป่า', lane: 'wilds', kind: 'stone', x: 2110, y: 1180, w: 12, d: 8, h: 4 });
add({ id: 'wolves.rock', place: 20, name: 'ทุ่งหมาป่าเงาจันทร์ ศิลาหอนจันทร์', lane: 'wilds', kind: 'stone', x: 2140, y: 1160, w: 6, d: 6, h: 6 });

// ── 21 · ผาแดง (the red cliff, 210 m) ───────────────────────────────────────────────────────
add({ id: 'redcliff.lookout', place: 21, name: 'ผาแดง จุดชมผาดาบชาด', lane: 'wilds', kind: 'tower', x: 2700, y: 2620, w: 8, d: 8, h: 3 });

// ── 22 · เกาะเมฆขาว · ศาลเทพทะเล ─────────────────────────────────────────────────────────────
add({ id: 'whitecloud.shrine', place: 22, name: 'ศาลเทพทะเล', lane: 'islands', kind: 'hall', x: 520, y: 206, w: 10, d: 8, h: 6 });
pillars('whitecloud.gate', 22, 'ศาลเทพทะเล ซุ้มประตู', 'islands', [[514, 222], [526, 222]], 2, 5);

// ── The passes out (warps to the zones that stay separate) on the outline's edge, and the south
// pass fort in the gorge (docs/archive/HUB.md §5: the south pass 440 m further south) ──────────────────
pillars('pass.south.gate', 0, 'ด่านใต้', 'roads', [[1465, 3340], [1485, 3340]], 4, 8);
pillars('pass.redcliff.gate', 0, 'ด่านผาแดง', 'roads', [[2550, 2935], [2570, 2935]], 4, 8);
pillars('pass.west.gate', 0, 'ด่านตะวันตก', 'roads', [[480, 2290], [480, 2311]], 4, 8);
pillars('pass.north.gate', 0, 'ช่องเขาเหนือ', 'roads', [[392, 610], [392, 631]], 4, 8);
add({ id: 'southpass.fort', place: 0, name: 'ป้อมด่านใต้', lane: 'roads', kind: 'hall', x: 1522, y: 3290, w: 30, d: 20, h: 8 });
add({ id: 'southpass.station', place: 0, name: 'ท่ารถม้าด่านใต้', lane: 'roads', kind: 'station', x: 1490, y: 3278, w: 8, d: 6, h: 4 });

/**
 * Every authored box, in stamping order (later boxes win where they overlap); the qinggong lane's
 * (stepping stones, boats, the gorge's ledges: yanyu.qinggong.ts) last.
 */
export const YANYU_BLOCKOUT: readonly BlockoutBox[] = [...boxes, ...YANYU_QINGGONG_BOXES];
